import _typeof from '@babel/runtime/helpers/typeof';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import FocusLock from 'react-focus-lock';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useControlledDropdown } from '../../hooks/use_controlled_dropdown.js';
import { neutralColors } from '../../styles/colors/colors.js';
import { spacing1x } from '../../styles/spacing.js';
import { isPlainObject } from '../../utils/lang.js';
import { bindRefs } from '../../utils/react.js';
import { noop } from '../../utils/util.js';
import { Badge } from '../Badge/Badge.js';
import { Box } from '../Box/Box.js';
import { DropdownBase } from '../DropdownBase/DropdownBase.js';
import { IconSearch } from '../Icons/IconSearch.js';
import { Input } from '../Input/Input.js';
import { Listbox } from '../Listbox/Listbox.js';
import { SelectButton } from './SelectButton.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var _excluded = ["aria-labelledby", "autoWidth", "buttonProps", "containerSelector", "defaultOption", "disabled", "emailStyle", "enableSelectAll", "error", "footer", "inline", "lightStyle", "multi", "onChange", "onHide", "optionHeight", "onShow", "options", "readOnly", "renderButton", "renderLabel", "renderOption", "searchable", "searchFilter", "showing", "startAdornment", "value"];
var DEFAULT_VALUE = {
  label: "No selection",
  value: "NO_SELECTION"
};
var MAX_HEIGHT = 490;
var HeaderContainer = styled.div.withConfig({
  displayName: "Select__HeaderContainer",
  componentId: "sc-besirf-0"
})(["&&{border-bottom:1px solid ", ";padding:5px;}&&:focus{outline:0;}"], neutralColors.silver);
var FooterWrapper = styled.div.withConfig({
  displayName: "Select__FooterWrapper",
  componentId: "sc-besirf-1"
})(["&&{border-top:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return "solid 1px " + theme.neutralColors.silver;
});

function defaultSearchFilter(value, option) {
  var searchValue = "".concat(option.label || option.value || "").toLowerCase();
  return searchValue.includes(value.toLowerCase());
}

function defaultRenderOption(option) {
  return option.label || option.value || "";
}

function renderMultiSelectLabel(selectedOptions) {
  var arr = selectedOptions.map(defaultRenderOption);
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return "".concat(arr[0], " and ").concat(arr[1]);
  if (arr.length < 9) return arr.slice(0, arr.length - 1).join(", ") + ", and " + arr[arr.length - 1];
  var first9 = arr.slice(0, 10);
  return first9.join(", ") + ", and ".concat(arr.length - 9, " more");
}

var getValueArr = function getValueArr(valueProp) {
  if (valueProp == void 0) return [];

  function stringify(val) {
    return isPlainObject(val) && "value" in val && (typeof val.value === "string" || typeof val.value === "number" || typeof val.value === "boolean") ? val.value : val;
  }

  return Array.isArray(valueProp) ? valueProp.map(stringify) : [stringify(valueProp)];
};

var Select = /*#__PURE__*/React.forwardRef(function Select2(props, elRef) {
  var ariaLabelledBy = props["aria-labelledby"],
      autoWidth = props.autoWidth,
      buttonProps = props.buttonProps,
      containerSelector = props.containerSelector,
      defaultOption = props.defaultOption,
      disabled = props.disabled,
      _props$emailStyle = props.emailStyle,
      emailStyle = _props$emailStyle === void 0 ? false : _props$emailStyle,
      enableSelectAll = props.enableSelectAll,
      error = props.error,
      footer = props.footer,
      _props$inline = props.inline,
      inline = _props$inline === void 0 ? true : _props$inline,
      _props$lightStyle = props.lightStyle,
      lightStyle = _props$lightStyle === void 0 ? false : _props$lightStyle,
      multi = props.multi,
      onChange = props.onChange,
      _props$onHide = props.onHide,
      onHide = _props$onHide === void 0 ? noop : _props$onHide,
      optionHeight = props.optionHeight,
      _props$onShow = props.onShow,
      onShow = _props$onShow === void 0 ? noop : _props$onShow,
      options = props.options,
      _props$readOnly = props.readOnly,
      readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
      renderButton = props.renderButton,
      _renderLabel = props.renderLabel,
      _props$renderOption = props.renderOption,
      renderOption = _props$renderOption === void 0 ? defaultRenderOption : _props$renderOption,
      searchableProp = props.searchable,
      _props$searchFilter = props.searchFilter,
      searchFilter = _props$searchFilter === void 0 ? defaultSearchFilter : _props$searchFilter,
      showing = props.showing,
      startAdornment = props.startAdornment,
      _props$value = props.value,
      valueProp = _props$value === void 0 ? [] : _props$value,
      rest = _objectWithoutProperties(props, _excluded);

  var _useControlledDropdow = useControlledDropdown({
    showing: showing
  }),
      showingState = _useControlledDropdow.showingState,
      setShowingState = _useControlledDropdow.setShowingState;

  var _React$useState = React.useState(true),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      shouldFocusButton = _React$useState2[0],
      setShouldFocusButton = _React$useState2[1];

  var searchable = searchableProp && options.length > 1;

  var _React$useState3 = React.useState(""),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      inputValue = _React$useState4[0],
      setInputValue = _React$useState4[1];

  var nextValueArr = getValueArr(valueProp);

  var _React$useState5 = React.useState(nextValueArr),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      valueState = _React$useState6[0],
      setValueState = _React$useState6[1];

  var nextValueHash = nextValueArr.join(",");
  var valueHash = React.useRef(nextValueHash);
  var staleValue = valueHash.current !== nextValueHash;
  var value = staleValue ? nextValueArr : valueState;
  var selectedOptions = options.filter(function (option) {
    return value.includes(option.value);
  });
  React.useEffect(function () {
    if (!staleValue) return;
    setValueState(nextValueArr);
    valueHash.current = nextValueHash;
  }, [nextValueHash, nextValueArr, staleValue]);
  var buttonRef = React.useRef(null);
  var boxRef = React.useRef(null);
  var listRef = React.useRef(null);
  var inputRef = React.useRef(null);

  var _useId = useId(1, "listbox-"),
      _useId2 = _slicedToArray(_useId, 1),
      listboxId = _useId2[0];

  var filterOptions = function filterOptions(option) {
    return searchFilter(inputValue || "", option);
  };

  var handleHide = function handleHide() {
    var _buttonRef$current;

    if (!shouldFocusButton) setShouldFocusButton(true);else (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
    onHide();
  };

  var handleShow = function handleShow() {
    var _listRef$current, _inputRef$current;

    if (!searchable) (_listRef$current = listRef.current) === null || _listRef$current === void 0 ? void 0 : _listRef$current.focus();else (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    onShow();
  };

  var handleButtonClick = function handleButtonClick(e) {
    setShowingState(!showingState);
    if (buttonProps !== null && buttonProps !== void 0 && buttonProps.onClick) buttonProps.onClick(e);
  };

  var handleInputChange = function handleInputChange(e) {
    setInputValue(e.target.value);
  };

  var handleListboxChange = function handleListboxChange(nextSelectedOptions) {
    var nextSelectedValues = nextSelectedOptions.map(function (option) {
      return option.value;
    });
    setValueState(nextSelectedValues);
    if (onChange) onChange(multi ? nextSelectedOptions : nextSelectedOptions[0]);
    if (!multi) setShowingState(false);
  };

  var handleBoxKeyDown = function handleBoxKeyDown(e) {
    switch (e.key) {
      case KEYS.TAB:
        {
          setShowingState(false);
          setShouldFocusButton(false);
        }
    }
  };

  var handleInputKeyDown = function handleInputKeyDown(e) {
    switch (e.key) {
      case KEYS.ARROW_DOWN:
        {
          var _listRef$current2;

          (_listRef$current2 = listRef.current) === null || _listRef$current2 === void 0 ? void 0 : _listRef$current2.focus();
          e.preventDefault();
          break;
        }

      case KEYS.ESCAPE:
        {
          setShowingState(false);
          e.stopPropagation();
        }
    }
  };

  var handleListboxKeyDown = function handleListboxKeyDown(e) {
    switch (e.key) {
      case KEYS.ESCAPE:
        {
          if (searchable) {
            var _inputRef$current2;

            setInputValue("");
            (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
            e.preventDefault();
          } else {
            setShowingState(false);
          }

          e.stopPropagation();
        }
    }
  };

  return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({}, rest), {}, {
    ref: elRef,
    tabIndex: -1,
    children: /*#__PURE__*/jsx(DropdownBase, {
      inline: inline,
      maxHeight: MAX_HEIGHT,
      showing: showingState,
      onHide: handleHide,
      onShow: handleShow,
      onOutsideClick: function onOutsideClick() {
        return setShowingState(false);
      },
      containerSelector: containerSelector,
      renderTrigger: function renderTrigger(_ref2) {
        var ref = _ref2.ref;
        var selection = multi ? selectedOptions : selectedOptions[0];
        var baseProps = {
          "aria-expanded": showingState,
          "aria-haspopup": "listbox",
          "aria-labelledby": ariaLabelledBy,
          disabled: disabled,
          error: error,
          onClick: handleButtonClick,
          ref: bindRefs(ref, buttonRef),
          selection: selection
        };

        if (typeof renderButton === "function") {
          return renderButton(baseProps);
        }

        delete baseProps.selection;
        return /*#__PURE__*/jsx(SelectButton, _objectSpread(_objectSpread(_objectSpread({}, buttonProps), baseProps), {}, {
          emailStyle: emailStyle,
          lightStyle: lightStyle,
          readOnly: readOnly,
          renderLabel: function renderLabel() {
            var count = selectedOptions.length;
            if (_renderLabel) return _renderLabel(selection);
            if (!multi) return defaultRenderOption(selectedOptions[0] || defaultOption || DEFAULT_VALUE);
            return /*#__PURE__*/jsxs(Fragment, {
              children: [count ? /*#__PURE__*/jsx(Badge, {
                style: {
                  marginInlineEnd: "10px"
                },
                count: count,
                selected: true,
                role: "none"
              }) : null, renderMultiSelectLabel(count ? selectedOptions : [defaultOption || DEFAULT_VALUE])]
            });
          }
        }));
      },
      renderContent: function renderContent(_ref3) {
        var _triggerRef$current, _triggerRef$current2;

        var ref = _ref3.ref,
            style = _ref3.style,
            triggerRef = _ref3.triggerRef;
        return /*#__PURE__*/jsx(Box, {
          "aria-expanded": searchable ? true : void 0,
          "aria-haspopup": searchable ? "listbox" : void 0,
          elevation: 2,
          onKeyDown: handleBoxKeyDown,
          ref: bindRefs(ref, boxRef),
          role: searchable ? "combobox" : void 0,
          style: _objectSpread({
            borderRadius: "3px",
            marginTop: spacing1x,
            maxWidth: autoWidth && ((_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.offsetWidth),
            minWidth: (_triggerRef$current2 = triggerRef.current) === null || _triggerRef$current2 === void 0 ? void 0 : _triggerRef$current2.offsetWidth,
            overflow: "hidden",
            overflowY: "auto",
            padding: 0,
            textAlign: "start",
            whiteSpace: "nowrap"
          }, style),
          children: /*#__PURE__*/jsxs(FocusLock, {
            autoFocus: false,
            disabled: !showingState,
            children: [searchable ? /*#__PURE__*/jsx(HeaderContainer, {
              children: /*#__PURE__*/jsx(Input, {
                "aria-label": "Search options",
                "aria-autocomplete": "list",
                "aria-controls": listboxId,
                startAdornment: IconSearch,
                onChange: handleInputChange,
                onKeyDown: handleInputKeyDown,
                ref: inputRef,
                style: {
                  width: "100%"
                },
                type: "text",
                value: inputValue
              })
            }) : null, /*#__PURE__*/jsx(Listbox, {
              "aria-labelledby": ariaLabelledBy,
              enableSelectAll: enableSelectAll,
              filterOptions: filterOptions,
              id: listboxId,
              multi: multi,
              onKeyDown: handleListboxKeyDown,
              onSelectedChange: handleListboxChange,
              optionHeight: optionHeight,
              options: options,
              ref: listRef,
              renderOption: renderOption,
              selectedOptions: selectedOptions,
              scrollRef: boxRef,
              startAdornment: startAdornment
            }), footer && /*#__PURE__*/jsx(FooterWrapper, {
              children: footer
            })]
          })
        });
      }
    })
  }));
});
Select.propTypes = {
  "aria-labelledby": _PropTypes.string,
  "autoWidth": _PropTypes.bool,
  "buttonProps": _PropTypes.object,
  "containerSelector": _PropTypes.string,
  "defaultOption": _PropTypes.object,
  "disabled": _PropTypes.bool,
  "emailStyle": _PropTypes.bool,
  "enableSelectAll": _PropTypes.bool,
  "error": _PropTypes.bool,
  "footer": _PropTypes.node,
  "inline": _PropTypes.bool,
  "lightStyle": _PropTypes.bool,
  "multi": _PropTypes.bool,
  "onChange": _PropTypes.func,
  "onHide": _PropTypes.func,
  "onShow": _PropTypes.func,
  "optionHeight": _PropTypes.oneOf(["short", "tall", "variable"]),
  "options": _PropTypes.arrayOf(_PropTypes.object).isRequired,
  "readOnly": _PropTypes.bool,
  "renderButton": _PropTypes.func,
  "renderLabel": _PropTypes.func,
  "renderOption": _PropTypes.func,
  "searchable": _PropTypes.bool,
  "searchFilter": _PropTypes.func,
  "showing": _PropTypes.bool,
  "startAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"".concat(propName, "\". Invalid React component with name \"").concat(name, "\" was supplied to \"").concat(componentName, "\". Only Icon components are allowed.\n"));
      }
    }
  },
  "value": _PropTypes.oneOfType([_PropTypes.string, _PropTypes.object, _PropTypes.arrayOf(_PropTypes.string), _PropTypes.arrayOf(_PropTypes.object)])
};
Select.displayName = "Select";

export { Select };
